import React from "react";
import styled from "styled-components";
import { formatDate } from "@backpacker/date";
import { GlossyThemeProvider } from "@glossy";
import { useCurrentUserLocale } from "@linkfluence/core/user/use-cases";
import { getContentClassificationLabel } from "@linkfluence/models/content-classification";
import { Locale } from "@linkfluence/models/locale";
import Loader from "@ui-kit/loader/dots";
import Text from "@radarly/components/cards/text";
import Layout from "@radarly/components/insight-pages/layout";
import Panel from "@radarly/components/panels/discovery-details";
import { useDiscoveryDetails } from "@radarly/core/insight-pages/use-cases";
import {
  useClosePanel,
  useOpenPostsPanel,
} from "@radarly/core/panel/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { DiscoveryDetails, DiscoveryRow } from "@radarly/models/discovery";
import { DiscoveryDetailsPanel } from "@radarly/models/panel";
import Card from "@radarly/shared/cards";

interface IProps {
  meta: DiscoveryDetailsPanel["meta"];
  detailsTemplate: DiscoveryDetailsPanel["detailsTemplate"];
}

const LoaderWrapper = styled.div`
  height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Root = styled.div`
  width: 100%;
  display: block;
`;

const getSubtitle = (meta: DiscoveryDetailsPanel["meta"], locale: Locale) => {
  const from = formatDate(meta.period.query.date.from, locale, "LL");
  const to = formatDate(meta.period.query.date.to, locale, "LL");
  return tr(locale, "INSIGHT_PAGES::DISCOVERY::DETAILS::HEADER::SUBTITLE", {
    from,
    to,
  });
};

const getTitle = (
  page: DiscoveryDetails,
  meta: DiscoveryRow["meta"],
  locale: Locale
) => {
  if (!page?.dynamicTitle) {
    return "...";
  }
  const { key, params = {} } = page.dynamicTitle;
  const field = meta.field.value;
  const variable = meta.value.value;

  return tr(locale, key || "DISCOVERY::SENTENCE::DETAILED_VIEW::TITLE", {
    ...params,
    "value.display":
      field === "content-classification"
        ? getContentClassificationLabel(variable, locale)
        : variable,
    query: params.queryLabel,
  });
};

const RadarlyDiscoveryDetailsPanel = ({ meta, detailsTemplate }: IProps) => {
  const onClosePanel = useClosePanel();
  const locale = useCurrentUserLocale();
  const project = useCurrentProject();
  const workspaceId = useCurrentWorkspaceId();
  const { page, loading } = useDiscoveryDetails(
    project.id,
    workspaceId,
    meta,
    detailsTemplate
  );

  const openPostsPanel = useOpenPostsPanel();
  return (
    <Panel
      onClose={() => onClosePanel("discovery-details")}
      locale={locale}
      field={meta.field.value as string}
      variable={meta.value.value}
      query={meta.queryLabel}
      subtitle={getSubtitle(meta, locale)}
      title={getTitle(page, meta, locale)}
    >
      <Root>
        {loading && (
          <LoaderWrapper>
            <Loader size="m" />
          </LoaderWrapper>
        )}
        {!loading && (
          <Layout
            renderCard={(card, height) => (
              <GlossyThemeProvider name="tile">
                <Card
                  card={card}
                  height={height}
                  timezone={page.timezone}
                  openPostsPanel={openPostsPanel}
                />
              </GlossyThemeProvider>
            )}
            renderTitle={(title, type, id) => (
              <Text content={title} isFirst={id === 0} type={type} noAnchor />
            )}
            rows={page.rows}
            locale={locale}
          />
        )}
      </Root>
    </Panel>
  );
};

export default RadarlyDiscoveryDetailsPanel;
